<template>
  <b-modal v-model="isActive" has-modal-card trap-focus>
    <div class="modal-card" style="width: 900px; max-width: 100%;">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ modalTitle }}</p>
      </header>
      <section class="modal-card-body">
        <b-loading :active="isCatalogsLoading" :is-full-page="false" />
        <div class="columns is-multiline">
          <div class="column is-4">
            <!-- Aviso rojo nativo de Buefy si el código ya está en uso (check reactivo). -->
            <b-field
              :label="$t('unique_material_id')"
              :type="codeInUseBy ? 'is-danger' : ''"
              :message="codeInUseBy ? $t('warehouse_material_code_in_use', { description: codeInUseBy.description }) : ''"
            >
              <b-input v-model="form.code" maxlength="100" @input="onCodeChanged" />
            </b-field>
          </div>
          <div class="column is-12">
            <b-field :label="$t('description')">
              <b-input v-model="form.description" type="textarea" @input="onDescriptionChanged" />
            </b-field>
            <!-- Banner alimentado por el 422 del backend al pulsar guardar. Reutiliza
                 .epc-alert--warning (global) + Tailwind para el layout interno. -->
            <div v-if="duplicateFound" class="epc-alert epc-alert--warning mt-2 flex-col items-stretch">
              <div class="epc-alert__text w-full">
                <b-icon pack="fas" icon="exclamation-triangle" size="is-small" />
                <span>{{ $t('warehouse_material_similar_name_detected') }}</span>
              </div>
              <div class="flex items-center justify-between w-full mt-1">
                <div class="min-w-0 flex-1">
                  <strong>{{ duplicateFound.code }}</strong> — {{ duplicateFound.description }}
                  <span class="ml-2 text-xs opacity-75">
                    {{ $tc('warehouse_material_inventory_lines_count', duplicateFound.inventory_lines_count, { count: duplicateFound.inventory_lines_count }) }}
                  </span>
                </div>
                <button type="button" class="epc-alert__btn" @click="useExistingMaterial(duplicateFound)">
                  {{ $t('warehouse_material_use_existing') }}
                </button>
              </div>
              <small class="w-full mt-1 opacity-75">{{ $t('warehouse_material_similar_name_hint') }}</small>
            </div>
          </div>

          <div class="column is-4">
            <b-field :label="$t('category')">
              <!-- Autocomplete contra taxonomies del proyecto (type=category); crea al vuelo si no existe. -->
              <warehouse-catalog-autocomplete
                v-model="form.category"
                :project-id="projectId"
                endpoint="taxonomies"
                response-key="taxonomies"
                order-field="order_column"
                value-field="name"
                :placeholder="$t('category')"
                :extra-params="{ type: 'category', only_active: 1 }"
                :create-payload-builder="(name) => ({ type: 'category', name })"
                create-on-missing
                :initial-option="form.category ? { name: form.category } : null"
              />
            </b-field>
          </div>
          <div class="column is-4">
            <b-field :label="$t('order_number')">
              <b-input v-model="form.order_number" maxlength="255" />
            </b-field>
          </div>
          <div class="column is-4">
            <b-field :label="$t('provider')">
              <b-select v-model="form.warehouse_provider_id" :placeholder="$t('select')" expanded>
                <option :value="null">-</option>
                <option v-for="item in providers" :key="'provider_' + item.id" :value="item.id">
                  {{ item.name }}
                </option>
              </b-select>
            </b-field>
          </div>

          <div class="column is-6">
            <b-field :label="$t('activity')">
              <b-select
                :value="parentActivityId"
                expanded
                :loading="isActivitiesLoading"
                @input="onParentActivityChange"
              >
                <option :value="null">-</option>
                <option
                  v-for="a in parentActivities"
                  :key="'act_' + a.id"
                  :value="a.id"
                >
                  {{ activityLabel(a) }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="column is-6">
            <b-field :label="$t('subactivity')">
              <b-select
                v-model="subactivityId"
                expanded
                :disabled="!parentActivityId || subactivities.length === 0"
                :loading="isActivitiesLoading"
              >
                <option :value="null">-</option>
                <option
                  v-for="s in subactivities"
                  :key="'sub_' + s.id"
                  :value="s.id"
                >
                  {{ activityLabel(s) }}
                </option>
              </b-select>
            </b-field>
          </div>

          <div class="column is-4">
            <b-field :label="$t('drawing_number')">
              <b-input v-model="form.drawing_number" maxlength="255" />
            </b-field>
          </div>
          <div class="column is-4">
            <b-field :label="$t('tag_kks')">
              <b-input v-model="form.tag_kks" maxlength="255" />
            </b-field>
          </div>
          <div class="column is-4">
            <b-field :label="$t('unit')">
              <b-select v-model="form.unit_id" :placeholder="$t('select')" expanded>
                <option v-for="unit in units" :key="'unit_' + unit.id" :value="unit.id">
                  {{ unit.name }}
                </option>
              </b-select>
            </b-field>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot justify-end">
        <v-button @click="close">{{ $t('cancel') }}</v-button>
        <v-button @click="submit" :disabled="isSaving" icon="save" color="green">
          {{ $t('save') }}
        </v-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import WarehouseCatalogAutocomplete from '../../project/warehouse/warehouse-catalog-autocomplete.vue'

const emptyForm = () => ({
  code: '',
  description: '',
  unit_id: null,
  category: '',
  order_number: '',
  warehouse_provider_id: null,
  drawing_number: '',
  tag_kks: '',
})

export default {
  name: 'warehouse-add-material-modal',
  components: { WarehouseCatalogAutocomplete },
  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
    /** Si es false, solo se expone `open()` (creación). Oculta edición por API pública del componente. */
    allowEdit: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    isActive: false,
    isSaving: false,
    isCatalogsLoading: false,
    isActivitiesLoading: false,
    units: [],
    providers: [],
    parentActivities: [],
    subactivities: [],
    parentActivityId: null,
    subactivityId: null,
    editingMaterialId: null,
    form: emptyForm(),
    duplicateFound: null,                 // material existente detectado por mismo nombre
    forceCreateDespiteSimilar: false,     // true tras confirmar "crear igualmente"
    similarCheckTimeout: null,            // handle del debounce al tipear descripción
    codeInUseBy: null,                    // material existente con el mismo code (UNIQUE)
    codeCheckTimeout: null,               // handle del debounce al tipear code
  }),
  computed: {
    ...mapGetters({
      company: 'app/company',
    }),
    modalTitle() {
      return this.editingMaterialId ? this.$t('edit') : this.$t('add_material')
    },
  },
  methods: {
    activityLabel(activity) {
      if (!activity) return ''
      const code = activity.planning_code ? `${activity.planning_code} - ` : ''
      return code + (activity.name || `#${activity.id}`)
    },
    async open() {
      this.editingMaterialId = null
      this.form = emptyForm()
      this.duplicateFound = null
      this.forceCreateDespiteSimilar = false
      this.codeInUseBy = null
      this.resetActivitySelection()
      await this.loadCatalogs()
      this.isActive = true
    },
    async openEdit(material) {
      if (!this.allowEdit) {
        return
      }
      const m = material || {}
      this.editingMaterialId = m.id != null ? Number(m.id) : null
      this.form = {
        code: m.code || '',
        description: m.description || '',
        unit_id: m.unit_id != null ? m.unit_id : null,
        category: m.category || '',
        order_number: m.order_number || '',
        warehouse_provider_id: m.warehouse_provider_id != null ? m.warehouse_provider_id : null,
        drawing_number: m.drawing_number || '',
        tag_kks: m.tag_kks || '',
      }
      this.duplicateFound = null
      this.forceCreateDespiteSimilar = false
      this.codeInUseBy = null
      this.resetActivitySelection()
      await this.loadCatalogs()
      await this.applyActivitySelectionFromMaterial(m)
      this.isActive = true
    },
    onDescriptionChanged() {
      // Al cambiar texto se pierde la confirmación previa y se relanza check con debounce.
      this.duplicateFound = null
      this.forceCreateDespiteSimilar = false
      clearTimeout(this.similarCheckTimeout)
      const term = (this.form.description || '').trim()
      if (!term) return
      this.similarCheckTimeout = setTimeout(() => this.checkSimilarName(term), 350)
    },
    async checkSimilarName(term) {
      if (!this.projectId) return
      try {
        const params = { name: term }
        if (this.editingMaterialId) params.exclude_material_id = this.editingMaterialId
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/warehouse/materials/check-similar`,
          { params },
        )
        // Solo mostramos aviso si la descripción no cambió mientras llegaba la respuesta.
        if ((this.form.description || '').trim() !== term) return
        // El endpoint devuelve matched_by para distinguir code vs name; aquí solo nos interesa name.
        const matched = data && data.matched_by === 'name' ? data.existing_material : null
        this.duplicateFound = matched || null
      } catch (e) {
        this.duplicateFound = null
      }
    },
    onCodeChanged() {
      // Mismo patrón que description: debounce y check reactivo.
      clearTimeout(this.codeCheckTimeout)
      this.codeInUseBy = null
      const term = (this.form.code || '').trim()
      if (!term) return
      this.codeCheckTimeout = setTimeout(() => this.checkCodeInUse(term), 350)
    },
    async checkCodeInUse(term) {
      if (!this.projectId) return
      try {
        const params = { code: term }
        if (this.editingMaterialId) params.exclude_material_id = this.editingMaterialId
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/warehouse/materials/check-similar`,
          { params },
        )
        if ((this.form.code || '').trim() !== term) return
        const matched = data && data.matched_by === 'code' ? data.existing_material : null
        this.codeInUseBy = matched || null
      } catch (e) {
        this.codeInUseBy = null
      }
    },
    useExistingMaterial(material) {
      // El usuario reutiliza en vez de crear. El padre refresca su listado.
      this.$emit('existing-selected', material)
      this.$emit('saved')
      this.close()
    },
    close() {
      this.isActive = false
    },
    resetActivitySelection() {
      this.parentActivityId = null
      this.subactivityId = null
      this.subactivities = []
    },
    async applyActivitySelectionFromMaterial(material) {
      const activityId = material.activity_id != null
        ? Number(material.activity_id)
        : (material.activity && material.activity.id != null ? Number(material.activity.id) : null)

      if (!Number.isFinite(activityId) || activityId < 1) {
        return
      }

      // Siempre resolver la actividad completa: material.activity suele venir sin parent_id.
      const activity = await this.fetchActivity(activityId)
      if (!activity) {
        return
      }

      const parentId = activity.parent_id != null && activity.parent_id !== ''
        ? Number(activity.parent_id)
        : null

      if (parentId) {
        this.parentActivityId = parentId
        await this.loadSubactivities(parentId)
        this.ensureSubactivityOption(activity)
        this.subactivityId = activityId
      } else {
        this.parentActivityId = activityId
        await this.loadSubactivities(activityId)
        this.subactivityId = null
      }
    },
    ensureSubactivityOption(activity) {
      if (!activity || activity.id == null) return

      const id = Number(activity.id)
      if (!Number.isFinite(id) || id < 1) return

      const exists = (this.subactivities || []).some((s) => Number(s.id) === id)
      if (exists) return

      this.subactivities = [
        ...(this.subactivities || []),
        {
          id,
          planning_code: activity.planning_code,
          name: activity.name,
        },
      ]
    },
    async fetchActivity(activityId) {
      const id = Number(activityId)
      if (!Number.isFinite(id) || id < 1) return null

      try {
        const { data } = await axios.get(`/api/v2/activity/${id}`, { params: { light: 1 } })
        if (data && data.success && data.data) {
          return data.data
        }
        if (data && data.id != null) {
          return data
        }
      } catch (error) {
        return null
      }
      return null
    },
    extractArrayFromResponse(data, key) {
      if (Array.isArray(data && data[key])) return data[key]
      if (Array.isArray(data && data.data && data.data[key])) return data.data[key]
      return []
    },
    async loadCatalogs() {
      this.isCatalogsLoading = true
      try {
        await Promise.all([
          this.loadUnits(),
          this.loadProviders(),
          this.loadParentActivities(),
        ])
      } finally {
        this.isCatalogsLoading = false
      }
    },
    async loadUnits() {
      if (!this.company || !this.company.id) {
        this.units = []
        return
      }

      try {
        const { data } = await axios.get(`/api/v2/company/${this.company.id}/measurement-units`)
        this.units = this.extractArrayFromResponse(data, 'units')
      } catch (error) {
        this.units = []
        this.$notify.error('error_loading_data')
      }
    },
    async loadProviders() {
      if (!this.projectId) {
        this.providers = []
        return
      }

      try {
        const { data } = await axios.get(`/api/v2/project/${this.projectId}/warehouse/providers`, {
          params: { per_page: 100, page: 1, order_field: 'name', order_dir: 'asc' },
        })
        this.providers = this.extractArrayFromResponse(data, 'providers')
      } catch (error) {
        this.providers = []
      }
    },
    async loadParentActivities() {
      if (!this.projectId) {
        this.parentActivities = []
        return
      }

      this.isActivitiesLoading = true
      try {
        const { data } = await axios.get('/api/v2/activities', {
          params: {
            p: this.projectId,
            per_page: 2000,
            page: 1,
            order_field: 'planning_code',
            order_dir: 'asc',
            light: 1,
          },
        })
        this.parentActivities = (data && Array.isArray(data.activities)) ? data.activities : []
      } catch (error) {
        this.parentActivities = []
      } finally {
        this.isActivitiesLoading = false
      }
    },
    async loadSubactivities(activityId) {
      const parentId = activityId != null && activityId !== '' ? Number(activityId) : null
      if (!Number.isFinite(parentId) || parentId < 1) {
        this.subactivities = []
        return
      }

      this.isActivitiesLoading = true
      try {
        const { data } = await axios.get(`/api/v2/activity/${parentId}/subactivities`, {
          params: { order_by: 'planning_code', order_dir: 'asc' },
        })
        this.subactivities = (data && Array.isArray(data.subactivities)) ? data.subactivities : []
      } catch (error) {
        this.subactivities = []
      } finally {
        this.isActivitiesLoading = false
      }
    },
    onParentActivityChange(val) {
      const n = val != null && val !== '' ? Number(val) : null
      this.parentActivityId = Number.isFinite(n) && n > 0 ? n : null
      this.subactivityId = null
      this.subactivities = []
      if (this.parentActivityId) {
        this.loadSubactivities(this.parentActivityId)
      }
    },
    buildPayload() {
      const activityId = this.subactivityId || this.parentActivityId || null

      return {
        code: this.form.code,
        description: this.form.description,
        unit_id: this.form.unit_id,
        category: this.form.category || null,
        order_number: this.form.order_number || null,
        warehouse_provider_id: this.form.warehouse_provider_id,
        drawing_number: this.form.drawing_number || null,
        tag_kks: this.form.tag_kks || null,
        activity_id: activityId,
      }
    },
    async submit() {
      if (!this.form.code || !this.form.description || !this.form.unit_id) {
        this.$notify.error('save_error')
        return
      }

      if (!this.projectId) return

      // Banner visible por el check reactivo → pedimos confirmación antes de llamar.
      if (this.duplicateFound && !this.forceCreateDespiteSimilar) {
        const ok = await this.confirmCreateDespiteDuplicate()
        if (!ok) return
        this.forceCreateDespiteSimilar = true
      }

      const payload = this.buildPayload()
      if (this.forceCreateDespiteSimilar) payload.force_create = true

      try {
        this.isSaving = true
        if (this.editingMaterialId) {
          await axios.put(`/api/v2/project/${this.projectId}/warehouse/materials/${this.editingMaterialId}`, payload)
        } else {
          await axios.post(`/api/v2/project/${this.projectId}/warehouse/materials`, payload)
        }
        this.$notify.success('save_successful')
        this.close()
        this.$emit('saved')
      } catch (error) {
        const response = error && error.response ? error.response : null
        const body = response && response.data ? response.data : null

        // Safety net: colisión que el check reactivo no vio (carrera entre tabs/sesiones).
        if (response && response.status === 422 && body && body.error === 'duplicate_name' && body.existing_material) {
          this.duplicateFound = body.existing_material
          const ok = await this.confirmCreateDespiteDuplicate()
          if (ok) {
            this.forceCreateDespiteSimilar = true
            this.isSaving = false
            await this.submit()
          }
          return
        }

        const codeErrors = body && body.errors ? body.errors.code : null
        const isDuplicatedCode = Array.isArray(codeErrors) && codeErrors.length > 0
        this.$notify.error(isDuplicatedCode ? 'existing_unique_material_code' : 'save_error')
      } finally {
        this.isSaving = false
      }
    },
    confirmCreateDespiteDuplicate() {
      return new Promise((resolve) => {
        this.$buefy.dialog.confirm({
          title: this.$t('warehouse_material_similar_name_detected'),
          message: this.$t('warehouse_material_confirm_create_duplicate'),
          cancelText: this.$t('cancel'),
          confirmText: this.$t('warehouse_material_create_anyway'),
          type: 'is-warning',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        })
      })
    },
  },
}
</script>
